/**
 * Loads optional theme components only when matching markup exists.
 *
 * Horizon supports dynamic section and cart rendering, so the observer stays
 * active for modules that have not appeared yet. Each module is imported at
 * most once; the browser's module map supplies the final CDN asset URL.
 */
const optionalComponents = [
  ['layered-slideshow-component', '@theme/layered-slideshow'],
  ['anchored-popover-component', '@theme/anchored-popover'],
  ['video-background-component', '@theme/video-background'],
  ['media-gallery', '@theme/media-gallery'],
  ['product-sku-component', '@theme/product-sku'],
  ['product-inventory', '@theme/product-inventory'],
  ['volume-pricing', '@theme/volume-pricing'],
  ['price-per-item', '@theme/price-per-item'],
  ['volume-pricing-info', '@theme/volume-pricing-info'],
].map(([selector, moduleName]) => ({ selector, moduleName, requested: false }));

function loadPresentComponents(root = document) {
  for (const component of optionalComponents) {
    if (component.requested) continue;

    const rootMatches = root instanceof Element && root.matches(component.selector);
    const descendantMatches = 'querySelector' in root && root.querySelector(component.selector);
    if (!rootMatches && !descendantMatches) continue;

    component.requested = true;
    import(component.moduleName).catch((error) => {
      component.requested = false;
      console.error(`[component-loader] Failed to load ${component.moduleName}`, error);
    });
  }
}

const observer = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element) loadPresentComponents(node);
    }
  }
});

function start() {
  loadPresentComponents();
  observer.observe(document, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
